// ============================================================================
// lib/platform.ts — détection plateforme runtime pour gates de conformité.
// ============================================================================
// GATE PRINCIPAL : App Store Guideline 3.1.1 interdit tout CTA d'achat ou lien
// vers un site marchand externe dans une app iOS qui donne accès à du contenu
// digital payant. Sur iOS natif on masque donc :
//   - les boutons « Souscrire / Débloquer / Acheter » qui ouvrent Stripe
//   - les liens « create-parent-link » (redirection Stripe partageable)
//   - le lien portail Stripe (« Gérer mon abonnement »)
//
// À la place : un CTA « Recevoir les infos par email » (opt-in explicite du
// user) qui déclenche `send-paywall-email`. C'est EXPLICITEMENT autorisé par la
// Guideline 3.1.3 mise à jour janvier 2024 : « Developers may communicate with
// their users outside of their app, including via email, about purchasing
// methods other than in-app purchase. »
//
// Web (marketing SPA + PWA) et Android : comportement inchangé — tous les
// CTAs Stripe restent actifs.
// ============================================================================

import { Capacitor } from '@capacitor/core';

/**
 * True si l'app tourne sur iOS natif (Capacitor iOS shell).
 * Gate obligatoire pour tout CTA d'achat ou lien vers Stripe / site marchand.
 * Try/catch défensif pour SSR/tests Node.
 */
export function isIOSNative(): boolean {
  try {
    return Capacitor.getPlatform() === 'ios';
  } catch {
    return false;
  }
}

/** True si l'app tourne sur Android natif. */
export function isAndroidNative(): boolean {
  try {
    return Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
}

/** True si l'app est packagée nativement (iOS OU Android), false sur web. */
export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * True quand le pointeur principal est tactile (natif, ou navigateur mobile).
 *
 * Sert à ne PAS `autoFocus` un champ texte au montage d'une page : sur tactile,
 * le focus ouvre le clavier virtuel, qui redimensionne le viewport et décale le
 * layout à l'arrivée. Au clavier physique, l'autofocus reste un gain.
 * Try/catch défensif : `matchMedia` peut manquer hors navigateur (tests Node).
 */
export function isTouchPrimary(): boolean {
  if (isNativeApp()) return true;
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}
